import { AnchorDirectionType, OriginDirectionType } from "../enums";
import { generateQueryFromMedia } from "../useMediaQuery/useMediaQuery";
import theme from "../theme/defaultTheme";
/**
 * @typedef {Object} PopperElement
 *
 * @property {Object} $anchor - Anchor absolute position info (top, left, offsetWidth, offsetHeight) of element
 * @property {Object} $content - Content absolute position info (offsetHeight, offsetWidth) of element
 * @property {string} direction - Direction property to get accurate measurement of the future position for Popover component
 * @property {number} offset - Offset intend for Popover component
 */

var isInYViewPort = function isInYViewPort(offsetHeight) {
  return offsetHeight < (window.innerHeight || document.documentElement.clientHeight);
};

var getWindowWidth = function getWindowWidth() {
  return window.innerWidth || document.documentElement.clientWidth;
};

function computeInViewPort(_ref) {
  var $anchor = _ref.$anchor,
      $content = _ref.$content,
      offset = _ref.offset;
  var inRightViewPort = offset + $anchor.left + $anchor.offsetWidth + $content.offsetWidth < getWindowWidth();
  var inLeftViewPort = $anchor.left - ($anchor.offsetWidth + $content.offsetWidth) > offset;
  var inTopViewPort = $anchor.top - $content.offsetHeight > offset;
  var inBottomViewPort = isInYViewPort(offset + $anchor.top + $anchor.offsetHeight + $content.offsetHeight);
  return {
    inRightViewPort: inRightViewPort,
    inLeftViewPort: inLeftViewPort,
    inTopViewPort: inTopViewPort,
    inBottomViewPort: inBottomViewPort
  };
}
/**
 * If the direction is not in the viewport, then we switch the direction
 *
 * @param {PopperElement}
 *
 * @returns the originDirection.
 */


function getOriginDirectionDesktopValue(_ref2) {
  var $anchor = _ref2.$anchor,
      $content = _ref2.$content,
      direction = _ref2.direction,
      offset = _ref2.offset;

  var _computeInViewPort = computeInViewPort({
    $anchor: $anchor,
    $content: $content,
    offset: offset
  }),
      inRightViewPort = _computeInViewPort.inRightViewPort,
      inLeftViewPort = _computeInViewPort.inLeftViewPort,
      inTopViewPort = _computeInViewPort.inTopViewPort,
      inBottomViewPort = _computeInViewPort.inBottomViewPort;

  var originDirection = direction;

  if (originDirection === OriginDirectionType.Top && !inTopViewPort) {
    originDirection = OriginDirectionType.Bottom;
  } else if (originDirection === OriginDirectionType.Bottom && !inBottomViewPort) {
    originDirection = OriginDirectionType.Top;
  }

  if (originDirection === OriginDirectionType.Left && !inLeftViewPort) {
    originDirection = OriginDirectionType.Right;
  } else if (originDirection === OriginDirectionType.Right && !inRightViewPort) {
    originDirection = OriginDirectionType.Left;
  }

  return originDirection;
}
/**
 * If the content is not in the viewport, then change the anchor direction
 *
 * @param {Omit<PopperElement, 'offset'>}
 *
 * @returns The direction of the anchor.
 */


function getAnchorDesktopDirection(_ref3) {
  var direction = _ref3.direction,
      $anchor = _ref3.$anchor,
      $content = _ref3.$content;
  var anchorDirection = direction;
  var inRightViewPort = $anchor.left + $content.offsetWidth <= getWindowWidth();
  var inLeftViewPort = $anchor.left - ($anchor.offsetWidth + $content.offsetWidth) >= 0;

  if (!inLeftViewPort && inRightViewPort) {
    anchorDirection = AnchorDirectionType.Start;
  }

  if (inLeftViewPort && !inRightViewPort) {
    anchorDirection = AnchorDirectionType.End;
  }

  if (!inLeftViewPort && !inRightViewPort) {
    anchorDirection = AnchorDirectionType.Center;
  }

  return anchorDirection;
}
/**
 * It returns the position of the content relative to the anchor, and the direction of the content relative to the anchor
 *
 * @param {PopperElement}
 *
 * @returns An object with two properties: position and originDirection.
 */


export function getOriginDesktopPosition(_ref4) {
  var $anchor = _ref4.$anchor,
      $content = _ref4.$content,
      offset = _ref4.offset,
      direction = _ref4.direction;
  var originDirection = getOriginDirectionDesktopValue({
    direction: direction,
    $anchor: $anchor,
    $content: $content,
    offset: offset
  });
  var position = {};

  switch (originDirection) {
    case OriginDirectionType.Left:
      position.left = $anchor.left - $content.offsetWidth - offset;
      break;

    case OriginDirectionType.Right:
      position.left = $anchor.left + $anchor.offsetWidth + offset;
      break;

    case OriginDirectionType.Top:
      position.top = $anchor.top - $content.offsetHeight + window.scrollY - offset;
      break;

    case OriginDirectionType.Bottom:
      position.top = $anchor.top + $anchor.offsetHeight + window.scrollY + offset;
      break;

    default:
      break;
  }

  return {
    position: position,
    originDirection: originDirection
  };
}
/**
 * It takes in an object with the following properties:
 *
 * - `originDirection`: the direction of the origin (left, right, top, bottom)
 * - `anchorDirection`: the direction of the anchor (start, center, end)
 * - `$anchor`: the anchor element
 * - `$content`: the content element
 *
 * It returns an object with the following properties:
 *
 * - `anchorDirection`: the direction of the anchor (start, center, end)
 * - `position`: the position of the content element
 *
 * @param {Pick<PopperElement, '$content | $anchor'> & originDirection & anchorDirection}
 *
 * @returns the position and anchorDirection of the content relative to the anchor
 */

export function getAnchorDesktopPosition(_ref5) {
  var originDirection = _ref5.originDirection,
      anchorDirection = _ref5.anchorDirection,
      $anchor = _ref5.$anchor,
      $content = _ref5.$content;
  var isVertical = originDirection === OriginDirectionType.Left || originDirection === OriginDirectionType.Right;
  var position = {};
  var direction = getAnchorDesktopDirection({
    $anchor: $anchor,
    $content: $content,
    direction: anchorDirection
  });

  if (direction === AnchorDirectionType.Center) {
    if (isVertical) {
      position.top = $anchor.top + $anchor.offsetHeight / 2 + window.scrollY - $content.offsetHeight / 2;
    } else {
      position.left = $anchor.left + $anchor.offsetWidth / 2 - $content.offsetWidth / 2;
    }
  }

  if (AnchorDirectionType.End === direction) {
    if (isVertical) {
      position.top = $anchor.top + $anchor.offsetHeight - $content.offsetHeight + window.scrollY;
    } else {
      position.left = $anchor.left - ($content.offsetWidth - $anchor.width);
    }
  }

  if (AnchorDirectionType.Start === direction) {
    if (isVertical) {
      position.top = $anchor.top + window.scrollY;
    } else {
      position.left = $anchor.left;
    }
  }

  return {
    anchorDirection: direction,
    position: position
  };
}
/**
 * If the anchor is not in the top viewport, then the direction is bottom. If the anchor is not in the bottom viewport,
 * then the direction is top. Otherwise, the direction is bottom
 *
 * @param {Omit<PopperElement, 'direction'>}
 *
 * @returns the direction of the origin.
 */

function getOriginMobileDirection(_ref6) {
  var $anchor = _ref6.$anchor,
      $content = _ref6.$content,
      _ref6$offset = _ref6.offset,
      offset = _ref6$offset === void 0 ? 4 : _ref6$offset;

  var _computeInViewPort2 = computeInViewPort({
    $anchor: $anchor,
    $content: $content,
    offset: offset
  }),
      inTopViewPort = _computeInViewPort2.inTopViewPort,
      inBottomViewPort = _computeInViewPort2.inBottomViewPort;

  var direction = OriginDirectionType.Bottom;

  if (!inTopViewPort) {
    direction = OriginDirectionType.Bottom;
  } else if (!inBottomViewPort) {
    direction = OriginDirectionType.Top;
  }

  return direction;
}
/**
 * It returns the position and direction of the content relative to the anchor
 *
 * @param {Omit<PopperElement, 'direction'>}
 *
 * @returns An object with two properties: position and originDirection.
 */


export function getOriginMobilePosition(_ref7) {
  var $anchor = _ref7.$anchor,
      $content = _ref7.$content,
      offset = _ref7.offset;
  var direction = getOriginMobileDirection({
    $anchor: $anchor,
    $content: $content,
    offset: offset
  });
  var position = {};

  switch (direction) {
    case OriginDirectionType.Top:
      position.top = $anchor.top - $content.offsetHeight + window.scrollY - offset;
      break;

    case OriginDirectionType.Bottom:
      position.top = $anchor.top + $anchor.offsetHeight + window.scrollY + offset;
      break;

    default:
      break;
  }

  return {
    position: position,
    originDirection: direction
  };
}
/**
 * If the anchor is not in the left viewport, but is in the right viewport, then the anchor direction is start
 *
 * @param {Omit<PopperElement, 'offset'>}
 *
 * @returns The direction of the anchor.
 */

function getAnchorMobileDirection(_ref8) {
  var direction = _ref8.direction,
      $anchor = _ref8.$anchor,
      $content = _ref8.$content;
  var anchorDirection = direction;

  var _computeInViewPort3 = computeInViewPort({
    $anchor: $anchor,
    $content: $content,
    offset: 0
  }),
      inLeftViewPort = _computeInViewPort3.inLeftViewPort,
      inRightViewPort = _computeInViewPort3.inRightViewPort;

  if (!inLeftViewPort && inRightViewPort) {
    anchorDirection = AnchorDirectionType.Start;
  }

  if (inLeftViewPort && !inRightViewPort) {
    anchorDirection = AnchorDirectionType.End;
  }

  if (!inLeftViewPort && !inRightViewPort) {
    anchorDirection = AnchorDirectionType.Center;
  }

  return anchorDirection;
}
/**
 * It takes an anchor direction, an anchor element, and a content element, and returns the position of the content element
 * relative to the anchor element, and the anchor direction
 *
 * @param {Omit<PopperElement, 'offset'>}
 *
 * @returns An object with two properties: position and anchorDirection.
 */


export function getAnchorMobilePosition(_ref9) {
  var direction = _ref9.direction,
      $anchor = _ref9.$anchor,
      $content = _ref9.$content;
  var computedDirection = getAnchorMobileDirection({
    direction: direction,
    $anchor: $anchor,
    $content: $content
  });

  var _computeInViewPort4 = computeInViewPort({
    $anchor: $anchor,
    $content: $content,
    offset: 0
  }),
      inRightViewPort = _computeInViewPort4.inRightViewPort;

  var position = {};

  if (!inRightViewPort) {
    position.left = $anchor.left + $anchor.width - $content.offsetWidth;
  } else {
    position.left = $anchor.left;
  }

  return {
    position: position,
    anchorDirection: computedDirection
  };
}
/**
 * Helper function to get screen constraint match
 * @param {('xs'|'sm'|'md'|'lg'|'xl'|'xlg')} maxWidth - Based on the Foundation-UI theme breakpoints
 *
 * @return {boolean}
 * */

export function isMatchScreen(maxWidth) {
  var query = generateQueryFromMedia({
    maxWidth: maxWidth
  }, theme.breakpoints.values);
  return window.matchMedia(query).matches;
}